import React, { useRef, useState } from "react";
import { IProgressIndicatorStyles, PrimaryButton, ProgressIndicator } from "@fluentui/react";
import { AppGlobals } from "./AppGlobals";
import axios from "axios";
import styles from "./styles/cr.module.scss";
import { CrTextField } from "./components/cr/CrTextField";
import { CustomSignInMenu } from "./CustomSignInMenu";
import { MenuToggle } from "./components/MenuToggle";
import { buildCustomSignInBreadcrumb } from "./breadcrumbs";
import { TopBannerCustomSignIn } from "./TopBannerCustomSignIn";

interface CustomSignInProps {
  onAccountSelection: () => void;
  demoEmail?: string | null;
}

export const getClientInfo = () => {
  const ua = navigator.userAgent;

  let browser = "Unknown";

  if (ua.includes("Edg/")) browser = "Edge";
  else if (ua.includes("Chrome")) browser = "Chrome";
  else if (ua.includes("Firefox")) browser = "Firefox";
  else if (ua.includes("Safari")) browser = "Safari";

  let os = "Unknown";
  if (ua.includes("Windows NT")) os = "Windows";
  else if (ua.includes("Mac OS X")) os = "Mac";
  else if (ua.includes("Android")) os = "Android";
  else if (ua.includes("iPhone") || ua.includes("iPad")) os = "iOS";

  const device =
    /Mobi|Android|iPhone|iPad/i.test(ua) ? "Mobile" : "Desktop";

  return {
    browser,
    os,
    device,
    userAgent: ua
  };
};

export const CustomSignIn = (props: CustomSignInProps): React.ReactElement => {

  const isDemo = !!props.demoEmail;

  const [email, setEmail] = useState<string>(props.demoEmail || "");
  const [password, setPassword] = useState<string>(isDemo ? "DemoPassword" : "");

  const [invalidLoginMsg, setInvalidLoginMsg] = useState<string>("");
  const [userAuthButNotFound, setUserAuthButNotFound] = useState<boolean>(false);

  const [progressHidden, setProgressHidden] = useState<boolean>(true);
  const [isNavigationVisible, setNavigationVisible] = useState(false);
  const isSubmittingRef = useRef(false);

  const toggleNavigation = () => setNavigationVisible(!isNavigationVisible);

  const breadcrumbItems = buildCustomSignInBreadcrumb();

  const handleLogin = async () => {
    if (isSubmittingRef.current) {
      return;
    }

    isSubmittingRef.current = true;

    try {
      setProgressHidden(false);

      const clientInfo = getClientInfo();
      const zedxApiURL = process.env.REACT_APP_ZedX_Non_Odata_API!;

      const response = await axios.post(
        `${zedxApiURL}/Accounts/Authenticate`,
        {
          username: email,
          password: password,
          customerRef: AppGlobals.CustomerRef,
          clientInfo
        }
      );

      if (response.data.token === "") {
        setProgressHidden(true);
        setUserAuthButNotFound(true);
      } else {
        AppGlobals.UserID = response.data.id;
        AppGlobals.Username = response.data.username;
        AppGlobals.UserTitle = response.data.name;
        AppGlobals.CustomJwtToken = response.data.token;
        AppGlobals.CustomerID = response.data.customerId;
        AppGlobals.CustomerTitle = response.data.customerTitle;

        setInvalidLoginMsg("");
        props.onAccountSelection();
      }

    } catch (error) {
      console.error("Authentication error:", error);
      setInvalidLoginMsg("Invalid Username or Password.");
      setProgressHidden(true);
    } finally {
      isSubmittingRef.current = false;
    }
  };

  const progressStyles: Partial<IProgressIndicatorStyles> = {
    itemProgress: {
      margin: 0,
      paddingTop: "3px"
    }
  };

  return (
    <div className="main">
      <div className="container">

        {/* LEFT NAVIGATION */}
        <div className={`navigation ${isNavigationVisible ? "show" : ""}`}>
          <CustomSignInMenu
            onCloseMenu={() => setNavigationVisible(false)}
          />
        </div>

        {/* BACKDROP */}
        <div
          className={`nav-backdrop ${isNavigationVisible ? "show" : ""}`}
          onClick={toggleNavigation}
          aria-hidden={!isNavigationVisible}
        />

        {/* RIGHT CONTENT AREA */}
        <div className="right-area">

          {/* TOP BANNER */}
          <div className="top-banner">
            <MenuToggle onToggleNavigation={toggleNavigation} />
            <TopBannerCustomSignIn breadcrumbItems={breadcrumbItems} />
          </div>

          {/* CONTENT */}
          <div
            className="content"
            style={{
              width: "100%",
              minWidth: 0,
              paddingLeft: "16px",
              paddingRight: "16px"
            }}
          >

            {/* PROGRESS BAR */}
            <div style={{ visibility: progressHidden ? "hidden" : "visible" }}>
              <ProgressIndicator barHeight={4} styles={progressStyles} />
            </div>

            <div className={styles.cr} style={{ maxWidth: 720 }}>

              <h2 style={{ color: "rgb(112,146,190)", fontWeight: "normal" }}>
                Welcome to ZedX Apps
              </h2>

              {!userAuthButNotFound && (
                <>
                  <p>
                    {isDemo
                      ? "You are accessing a demo account. Click login to continue."
                      : "To login to ZedX Apps, enter your email address and password then click on the button below."}
                  </p>

                  <div style={{ maxWidth: "500px" }}>
                    <CrTextField
                      label="Email Address"
                      maxLength={500}
                      className={styles.formField}
                      value={email}
                      onChange={(_, newValue) => setEmail(newValue || "")}
                      required
                      type="email"
                      disabled={isDemo}
                    />

                    <CrTextField
                      label="Password"
                      value={password}
                      className={styles.formField}
                      onChange={(_, newValue) => setPassword(newValue || "")}
                      required
                      type="password"
                      disabled={isDemo}
                    />

                    <PrimaryButton text="Login" onClick={handleLogin} disabled={!progressHidden} />

                    {invalidLoginMsg && (
                      <div style={{ marginTop: 20, color: "red" }}>
                        {invalidLoginMsg}
                      </div>
                    )}
                  </div>
                </>
              )}

              {userAuthButNotFound && (
                <div>
                  Your login details were validated with our central authentication
                  system, but we could not find a record in our database for the
                  email address '{email}'. Please contact your administrator.
                </div>
              )}

            </div>
          </div>
        </div>
      </div>
    </div>
  );
};